/**
 * QuickstartPage — first-login walkthrough with real product screenshots.
 *
 * Shown automatically after a doctor's first login (localStorage flag),
 * and always reachable from Settings → "View Quickstart Guide" or /quickstart.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mic, Wand2, FileText, CalendarDays, BookTemplate, Brain, Sparkles, ShieldCheck, ClipboardCheck, ArrowRight } from 'lucide-react'
import { useStore } from '../lib/store'
import { api } from '../lib/api'

export const QUICKSTART_SEEN_KEY = 'scribey_quickstart_seen'

/** Lightweight localStorage-only flag (legacy / fast cache). */
export function markQuickstartSeen() {
  try { localStorage.setItem(QUICKSTART_SEEN_KEY, '1') } catch {}
}

/**
 * Returns true only when the doctor hasn't seen the quickstart yet —
 * checked server-side (doctor.settings.quickstart_seen) AND via localStorage
 * (legacy cache / Safari persistence). Both must be absent to show the tour.
 */
export function shouldShowQuickstart(doctor?: { settings?: Record<string, any> } | null): boolean {
  if (!doctor) return false
  if (doctor.settings?.quickstart_seen) return false
  try { return !localStorage.getItem(QUICKSTART_SEEN_KEY) } catch { return false }
}

interface Step {
  icon: React.ReactNode
  title: string
  body: React.ReactNode
  image?: string
  imageAlt?: string
  tips?: string[]
}

export function QuickstartPage() {
  const navigate = useNavigate()
  const { markQuickstartSeen: markSeen, doctor, updateDoctor } = useStore()

  // Phase 2: one-time recording-law acknowledgment. Pre-checked only if already acked.
  const [recordingAck, setRecordingAck] = useState<boolean>(!!(doctor as any)?.recording_ack_at)

  // Mark as seen on first view — covers direct /quickstart visits and back-button exits.
  // Both this page and WelcomeTourPage mark seen (idempotent, harmless).
  useEffect(() => { markSeen() }, [markSeen])

  async function finish() {
    markSeen()
    if (recordingAck && !(doctor as any)?.recording_ack_at) {
      try {
        const updated = await api.updateProfile({ recording_ack: true })
        updateDoctor(updated)
      } catch (err) { console.warn('Failed to record acknowledgment:', err) }
    }
    navigate('/')
  }

  const steps: Step[] = [
    {
      icon: <Mic size={22} />,
      title: '1. Just hit Capture and talk',
      body: (
        <>
          The fastest way to use Dr. Scribey: tap <strong>Capture</strong> on the dashboard and start
          your visit or dictation. No forms first — say the patient's name and what kind of visit it is,
          and the AI figures out the rest.
        </>
      ),
      image: '/qs-shots/01-dashboard.png',
      imageAlt: 'Dashboard with Capture button and schedule',
      tips: [
        'Auto-detection: say "This is a follow-up on Maria Santos for right knee pain" — the patient name and visit type are detected and filled in automatically.',
        'Works offline: if you lose wifi (hospital basement, anyone?), keep dictating. Audio is saved securely on your device and transcribed when you\'re back online.',
        'Your screen stays awake while recording — no need to keep tapping it.',
      ],
    },
    {
      icon: <CalendarDays size={22} />,
      title: '2. Import your day with one photo',
      body: (
        <>
          Snap a photo of your printed schedule (or the OR board) and use{' '}
          <strong>Import from photo</strong> on the dashboard. Dr. Scribey reads it and pre-creates
          your patient list for the day, so each visit is one tap away.
        </>
      ),
      image: '/qs-shots/08-schedule.png',
      imageAlt: 'Dashboard showing an imported daily patient schedule',
      tips: [
        'Each scheduled visit becomes a ready-to-record encounter — tap it when the patient is in the room.',
        'No schedule? No problem. Capture works standalone anytime.',
      ],
    },
    {
      icon: <Mic size={22} />,
      title: '3. While you’re recording',
      body: (
        <>
          During the visit you’ll see a timer, a live waveform, and your words appearing as you
          speak. When the visit is over, <strong>slide the bar at the bottom</strong> to end the
          recording — it’s a slide instead of a button so a stray tap can’t accidentally cut
          off a visit. Your note starts writing itself the moment you slide.
        </>
      ),
      image: '/qs-shots/10-recording.png',
      imageAlt: 'Live recording screen with timer, waveform and slide-to-end bar',
      tips: [
        'Long-press to pause (hallway interruptions, phone calls).',
        'You can lock your phone or switch apps — recording keeps going.',
        'Lost signal? Keep talking — audio is saved on your device and transcribed when you’re back online.',
      ],
    },
    {
      icon: <Wand2 size={22} />,
      title: '4. Direct the note with your voice',
      body: (
        <>
          You're in control while you dictate. The AI listens for your commands, not just your words:
        </>
      ),
      image: '/qs-shots/02-new-visit.png',
      imageAlt: 'New Visit page with note types',
      tips: [
        '"Use my usual knee exam" — inserts your saved standard exam.',
        '"The exam is the same as last time" — carries the exam forward from the patient\'s prior note.',
        '"Include risks and benefits of the procedure" — the note will list them, not just say "discussed".',
        '"Scratch that — change the dose to 20 milligrams" — corrections mid-dictation just work.',
        '"End note" or "stop recording" — say it and the visit seals itself, hands-free.',
        'Op Note, Office Visit, Consult, Rounding, Procedure, Telehealth — pick a type or let it auto-detect.',
      ],
    },
    {
      icon: <ClipboardCheck size={22} />,
      title: '5. What the scribe expects from you',
      body: (
        <>
          Dr. Scribey documents <strong>only what you say</strong> — it will never invent findings,
          and it never writes “not discussed” filler. Anything you don’t mention is simply left
          out, the way a good human scribe would. That means a few two-second phrases go a long way:
        </>
      ),
      tips: [
        '"ROS otherwise negative" — prints the full attestation: “All other systems reviewed and negative.” Without it, only the systems you mentioned appear.',
        '"Right hip exam normal except tender over the trochanter" — prints the complete normal hip exam with your one exception woven in. “Normal” is shorthand the scribe expands.',
        'If you only dictate two findings, the exam shows exactly those two findings — nothing is auto-filled as normal unless you attest to it.',
        'Why so strict? Auto-documented negatives you never performed are a chart-audit red flag. Your two-word attestations keep every line of the note defensible — and yours.',
      ],
    },
    {
      icon: <FileText size={22} />,
      title: '6. Review, polish, approve',
      body: (
        <>
          When you end the visit, a structured note is generated and <strong>quality-checked against
          the transcript</strong> — every claim must be traceable, laterality is verified, and your
          dictated commands are confirmed to be followed. Edit any section, then{' '}
          <strong>Approve</strong>.
        </>
      ),
      image: '/qs-shots/09-quality-flags.png',
      imageAlt: 'Note with the quality bar expanded showing detailed warning flags',
      tips: [
        'Tap the quality bar to see exactly what the checker is worried about — each flag shows the section, the concern, and a suggested fix.',
        'Copy individual sections or the full note straight into your EMR (set your EMR in Settings).',
        'Codes tab suggests CPT/ICD-10 from the documentation.',
        'Generate patient instructions or referral letters from the same visit with one tap.',
      ],
    },
    {
      icon: <BookTemplate size={22} />,
      title: '7. Templates: start from your style',
      body: (
        <>
          Browse the template library (filtered to your specialty) and import the ones you use, or
          create your own. Templates give the AI your preferred structure for each note type.
        </>
      ),
      image: '/qs-shots/04-templates.png',
      imageAlt: 'Template library with specialty filters',
      tips: [
        'Imported templates become "yours" — edit them freely under My Templates.',
        'Saved phrases ("my usual post-op instructions") can be invoked by voice while dictating.',
      ],
    },
    {
      icon: <Brain size={22} />,
      title: '8. It learns how you write',
      body: (
        <>
          Every edit you make teaches Dr. Scribey your style — phrasing, structure, what you include
          and what you skip. Check the <strong>AI Learning</strong> tab to see your style profile
          build over time. The more you use it, the less you edit.
        </>
      ),
      tips: [
        'Style is learned per note type — your op notes and office notes can have different voices.',
        'Approved notes become examples; rejected phrasings are remembered too.',
      ],
    },
    {
      icon: <Sparkles size={22} />,
      title: '9. Ask the assistant anything',
      body: (
        <>
          The ✨ button in the top bar opens your AI assistant. Ask about your patients ("when did I
          last see Mrs. Santos?"), get help with documentation, or draft a letter — it knows your
          practice context.
        </>
      ),
      image: '/qs-shots/07-assistant.png',
      imageAlt: 'AI assistant drawer open over the dashboard',
    },
    {
      icon: <ShieldCheck size={22} />,
      title: 'Built for PHI from day one',
      body: (
        <>
          Encrypted in transit and at rest, audit-logged, and BAA available. Audio recordings are{' '}
          <strong>encrypted on your device</strong> and <strong>deleted the moment they’re
          transcribed</strong> — the written note is the medical record, not the recording. Your
          data is exportable and your account is deletable — see Settings. Full details on the
          Security page.
        </>
      ),
    },
  ]

  return (
    <div className="max-w-3xl mx-auto pb-24 px-1">
      <div className="text-center pt-6 pb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Welcome to Dr. Scribey 👋
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Two minutes to learn it. A lifetime of charting time back.
        </p>
      </div>

      <div className="space-y-10">
        {steps.map((step, i) => (
          <section
            key={i}
            className="bg-gray-100 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-brand-500/15 text-brand-500 flex items-center justify-center flex-shrink-0">
                {step.icon}
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{step.title}</h2>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{step.body}</p>
            {step.image && (
              <img
                src={step.image}
                alt={step.imageAlt || step.title}
                loading="lazy"
                className="rounded-xl border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-xs mx-auto block mb-4"
              />
            )}
            {step.tips && (
              <ul className="space-y-2">
                {step.tips.map((tip, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <span className="text-brand-500 mt-0.5 flex-shrink-0">•</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {/* NEEDS-LEGAL-REVIEW: recording-law acknowledgment copy pending attorney review before prod. */}
      <div className="mt-10 max-w-xl mx-auto bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={recordingAck}
            onChange={(e) => setRecordingAck(e.target.checked)}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 flex-shrink-0"
          />
          <span className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
            I understand that recording laws vary by state and that some states require the consent of all parties before recording a conversation. I am responsible for obtaining any patient consent required in my jurisdiction before using Dr. Scribey to capture an encounter.
          </span>
        </label>
      </div>

      <div className="mt-6 text-center">
        <button
          onClick={finish}
          disabled={!recordingAck}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 text-white px-8 py-4 rounded-full font-bold text-lg shadow-xl transition-all hover:scale-105 active:scale-95"
        >
          Start my first visit
          <ArrowRight size={20} />
        </button>
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          {recordingAck ? 'You can revisit this guide anytime from Settings.' : 'Please acknowledge the note above to continue.'}
        </p>
      </div>
    </div>
  )
}
